import classNames from 'classnames';
import * as React from 'react';
import {
	reduxForm,
	SubmissionError,
	formValueSelector,
	InjectedFormProps,
} from 'redux-form';

import { CartItem } from '@aicpcu/web-api-spec-model';

import { AddCartButton } from '@app/components/add-cart-button';
import { PurchaseMaterialsOverlay } from '@app/components/card-tab-overlay';
import { Select, FormContainer, RadioGroupField } from '@app/components/forms';
import {
	examWindowRequired,
	materialRequired,
} from '@app/components/forms/validations.constants';
import { Loader } from '@app/components/loader';
import { Notification } from '@app/components/notification';
import { FormValues } from '@app/interfaces/form';
import { Store } from '@app/interfaces/store/store';
import { gtmAddToCart } from '@app/utility/gtm';
import { getAlphaOrder, isRecommended } from '@app/utility/sort';

import { PURCHASE_TAB_STRINGS, TabIdType } from './purchase-form.constants';
import {
	MaterialExamWindowOptions,
	MaterialOption,
	PropMaterialRadio,
} from './purchase-form.types';
import {
	formUtil,
	isVirtualExam,
	localStorageUtil,
	setUpdateCookie,
	tabUtil,
} from './purchase-form.util';
import EnrollCardButton from '../new-add-cart-button';

type PropPurchaseTabMaterial = {
	actions: any;
	courseCode: string;
	description: string;
	examWindowOptions?: MaterialExamWindowOptions[];
	form: string;
	hasNoExamWindows?: boolean;
	hidden: boolean;
	id: number;
	isSingleTab: boolean;
	onSelect: (tabId: TabIdType) => void;
	setContainerHeight: (value: string, tabId: TabIdType) => void;
	materialsNote?: string;
	store: Store;
};

/**
 * Sort material options by recommended first, then alpabetical.
 *
 * @param {MaterialOption} optionA Material option A.
 * @param {MaterialOption} optionB Material option B.
 * @returns {number} Sort order value.
 */
const sortMaterials = (
	{ label: a }: MaterialOption,
	{ label: b }: MaterialOption,
): number => {
	if (isRecommended(a)) {
		return isRecommended(b) ? getAlphaOrder(a, b) : -1;
	}
	return isRecommended(b) ? 1 : getAlphaOrder(a, b);
};

/**
 * Map material option fields provided to radio options.
 *
 * @param {MaterialOption} materialOption Material options.
 * @returns {PropMaterialRadio} Radio options.
 */
const mapRadioProperties = ({
	label: title,
	value,
	description,
	currentPrice,
	saleEndDate,
	salePrice,
}: MaterialOption): PropMaterialRadio => ({
	className: 'purchase-tabs__material-item custom__rb--gray',
	label: `${title.trim()}, $${salePrice.toString()}`,
	labelChildren: {
		className: 'purchase-tabs__product-info',
		description,
	},
	name: 'materials-radio-option',
	currentPrice,
	saleEndDate,
	salePrice,
	required: true,
	title,
	value,
});

const { MATERIAL_TAB } = PURCHASE_TAB_STRINGS;

/**
 * Purchase Material Form is Redux Form.
 *
 * Depending on an exam window selection, a radio group of course materials
 * will be available. Upon successful form submission, an overlay is presented.
 *
 * Any parameter with "Redux or Redux Form" descriptor is connected and
 * composed.
 *
 * @param {Object} store                Auth store.
 * @param {Object} actions             Redux Actions.
 * @param {Array}   examWindowOptions   Exam window options for select option.
 * @param {boolean} isSingleTab        Is a single form, with no matching form.
 * @param {boolean} hasNoExamWindows        Has no exam windows. Defaults to first window.
 * @callback onSelect - Handle overlay tab selection.
 *
 * @returns {React.Element} Purchase material form component.
 */
const PurchaseTabMaterialRaw: React.FC<
	InjectedFormProps<{}, PropPurchaseTabMaterial> & PropPurchaseTabMaterial
> = ({
	actions: { addToCart, getCart },
	courseCode,
	description,
	examWindowOptions,
	hasNoExamWindows = false,
	hidden,
	id,
	isSingleTab,
	onSelect,
	setContainerHeight,
	store,
	materialsNote,
	...reduxFormProps
}: InjectedFormProps<{}, PropPurchaseTabMaterial> &
	PropPurchaseTabMaterial) => {
	const { cart, content } = store;
	const { cartItems }: { cartItems: CartItem[] } = cart;
	const {
		change,
		form,
		handleSubmit,
		reset,
		submitFailed,
		submitSucceeded,
		submitting,
		valid,
	} = reduxFormProps;

	// Field names based on course's ID.
	const { EXAMWINDOW_FIELDNAME, MATERIAL_FIELDNAME } = Object.freeze(
		formUtil.createMaterialFields(id),
	);

	const panelId = tabUtil.setPanelId(MATERIAL_TAB.id, id);

	// Create form value selector that's connected to redux.
	const selector = formValueSelector<Store>(form);
	const selectedExamWindowApiId = selector(store, EXAMWINDOW_FIELDNAME);
	const selectedMaterialId = selector(store, MATERIAL_FIELDNAME);

	const [overlayVisible, setOverlayVisible] = React.useState(false);
	const [storedTitle, setStoredTitle] = React.useState('');
	const [submitError, setSubmitError] = React.useState();
	const [isShowEnroll, setIsShowEnroll] = React.useState<boolean>();

	// Memoized radio options based on current exam window selection.
	const radioOptions = (() => {
		if (!selectedExamWindowApiId) {
			return [];
		}

		const selectedExamWindow = examWindowOptions?.find(
			({ value }) => value === selectedExamWindowApiId.toString(),
		);
		const { options = [] } = selectedExamWindow || {};
		return options.sort(sortMaterials).map(mapRadioProperties);
	})();

	// Selected material.
	const selectedMaterial = (() =>
		radioOptions.find(({ value }) => value === selectedMaterialId))();

	// Memoized matching VE cart item.
	const [hasMatchingVirtualExam, hasMatchingExamWindow] = React.useMemo(() => {
		const matchingExam = cartItems.find((item) => {
			const { courseCode: code = '', itemId = '' } = item;
			if (code === courseCode && content.programRule?.products?.[itemId]) {
				const { gradeForExam, published = false } =
					content.programRule.products[itemId];
				return isVirtualExam(published, gradeForExam);
			}

			return false;
		});

		const { targetSeries: matchingSeries = '' } = matchingExam || {};
		return [
			matchingExam !== undefined,
			matchingExam !== undefined &&
				selectedExamWindowApiId !== undefined &&
				matchingSeries !== selectedExamWindowApiId,
		];
	}, [cartItems, content, courseCode, selectedExamWindowApiId]);

	// Logic to handle purchase form with hidden exam window options.
	const setInitialExamWindow = React.useCallback(() => {
		if (examWindowOptions?.length) {
			change(EXAMWINDOW_FIELDNAME, examWindowOptions[0].value);
		}
	}, [change, EXAMWINDOW_FIELDNAME, examWindowOptions]);

	// Reset form will reset form, and set initial window if No Window is set.
	const resetForm = () => {
		reset();
		setOverlayVisible(false);

		if (hasNoExamWindows) {
			setInitialExamWindow();
		}
	};

	// Use effect to load title and overlay state from local storage.
	React.useEffect(() => {
		const handleLoadForm = (isOpen: boolean, title: string) => {
			setOverlayVisible(isOpen);
			setStoredTitle(title);
		};

		localStorageUtil.loadForm(handleLoadForm, courseCode);
	}, [
		courseCode,
		hasNoExamWindows,
		setInitialExamWindow,
		setOverlayVisible,
		setStoredTitle,
	]);

	// Dispatch immediate exam window selection if No Window is set.
	React.useEffect(() => {
		if (hasNoExamWindows) {
			setInitialExamWindow();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Redux form submission handler.
	 *
	 * - Sets an UPDATE_CART cookie for unauthenticated user with no cart.
	 * - Adds selected material to Cart.
	 * - Adds selected material to Google Tag Manager.
	 *
	 * @params {Array} values Redux Form submission values
	 */
	const handleSubmission = async (values: FormValues) => {
		setUpdateCookie(store.auth.isAuthenticated);

		const { type, payload } = await addToCart({
			itemId: parseInt(values[MATERIAL_FIELDNAME], 10),
			series: values[EXAMWINDOW_FIELDNAME],
		});

		if (type === 'API_ERROR') {
			setSubmitError(payload);
			throw new SubmissionError({});
		}

		await gtmAddToCart(parseInt(values[MATERIAL_FIELDNAME], 10));

		await getCart('inline');

		setStoredTitle(selectedMaterial?.title || '');
		reset();
	};

	return (
		<div
			className="purchase-tabs__panel"
			id={panelId}
			role={!isSingleTab ? 'tabpanel' : ''}
			aria-labelledby={!isSingleTab ? panelId : ''}
			hidden={hidden}
		>
			<p>{description}</p>
			{materialsNote && (
				<Notification className="u-margin-bottom">
					{/* eslint-disable-next-line react/no-danger */}
					<div dangerouslySetInnerHTML={{ __html: materialsNote }} />
				</Notification>
			)}
			<FormContainer
				isRedux
				className={classNames('purchase-tabs__purchase-form', {
					'has-loader': submitting,
				})}
				onSubmit={handleSubmit(handleSubmission)}
			>
				{submitting && <Loader isVeiled />}

				<ul className="u-list-unstyled">
					<li
						className={classNames('form__item', {
							'u-hidden': hasNoExamWindows,
						})}
					>
						<Select
							id={EXAMWINDOW_FIELDNAME}
							name={EXAMWINDOW_FIELDNAME}
							label="Select an Anticipated Exam Window"
							options={examWindowOptions}
							validate={[examWindowRequired]}
							tooltipMessage="We regularly update our content to provide you
              with the most up-to-date skills. Select your expected exam
              window to ensure you purchase the most current materials."
							onChange={() => reset()}
						/>
					</li>

					<li className="form__item">
						{!!radioOptions.length && (
							<RadioGroupField
								id={MATERIAL_FIELDNAME}
								name={MATERIAL_FIELDNAME}
								className="purchase-tabs__materials u-padding-bottom"
								options={radioOptions}
								validate={[materialRequired]}
								label="Choose Materials"
								isSrOnly
							/>
						)}
					</li>
				</ul>

				{hasMatchingExamWindow && (
					<Notification className="u-margin-bottom">
						The window you selected to take your exam does not match the
						anticipated exam window of your materials.
					</Notification>
				)}
				{!isShowEnroll && (
					<AddCartButton
						className={classNames({
							'btn--disabled': !valid,
						})}
						product={selectedMaterial?.title}
						price={selectedMaterial?.salePrice}
						error={submitFailed && submitError}
						isInForm
					/>
				)}
			</FormContainer>
			<EnrollCardButton
				price={selectedMaterial?.salePrice!}
				itemId={selectedMaterial?.value ? +selectedMaterial?.value : 0}
				learnMoreUrl=""
				showCartButton={false}
				learnMoreText=""
				learnMoreExternal=""
				setIsShowEnroll={setIsShowEnroll}
				hideOnCart
			/>

			<PurchaseMaterialsOverlay
				courseCode={courseCode}
				hasMatchingCartItem={hasMatchingVirtualExam}
				isAuthenticated={store.auth.isAuthenticated}
				isSingleForm={isSingleTab}
				loginUrl={store.auth.loginUrl}
				onClose={resetForm}
				onSelect={onSelect}
				show={overlayVisible || submitSucceeded}
				title={storedTitle}
				setContainerHeight={setContainerHeight}
			/>
		</div>
	);
};

export const PurchaseTabMaterial = reduxForm<{}, PropPurchaseTabMaterial>({})(
	PurchaseTabMaterialRaw,
);
